import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Currency } from '~/components/Currency/Currency.hooks';
import { HttpError } from '~/components/Error/HttpError';
import { UseDosiminQueryOptions } from '~/components/Query/Query.hooks';

export type BoostDetail = {
  level: number;
  itemType: 'CITIZEN' | 'PET' | string;
  boost: number;
};

export type ChallengeBoostCriteria = {
  maxBoost: number;
  maxBoostPerLevelCount: number;
  maxPetBoost: number;
  boostDetailList: BoostDetail[];
};

export type CitizenBoostDetail = {
  nftType: 'CITIZEN';
  level: number;
  growth: 'CITIZEN';
  count: number;
  boost: number;
};

export type FriendBoostDetail = {
  nftType: 'PET';
  level: number;
  growth: string;
  count: number;
  boost: number;
};

export type ChallengeBoost = {
  totalBoost: number;
  citizenBoost: number;
  petBoost: number;
  citizenBoostDetailList: CitizenBoostDetail[];
  petBoostDetailList: FriendBoostDetail[];
};

export const challengeBoostUrl = '/api/citizen/v1/challenge/boost';
export const getChallengeBoost = async () => {
  const { data } = await axios.get(challengeBoostUrl);
  return data;
};

export const useChallengeBoost = <T = ChallengeBoost>(
  options?: UseDosiminQueryOptions<ChallengeBoost, HttpError, T>
) =>
  useQuery<ChallengeBoost, HttpError, T>({
    queryKey: ['challenge', 'boost'],
    queryFn: getChallengeBoost,
    initialData: {
      totalBoost: 0,
      citizenBoost: 0,
      petBoost: 0,
      citizenBoostDetailList: [
        {
          nftType: 'CITIZEN',
          level: 2,
          growth: 'CITIZEN',
          count: 0,
          boost: 0,
        },
        {
          nftType: 'CITIZEN',
          level: 3,
          growth: 'CITIZEN',
          count: 0,
          boost: 0,
        },
        {
          nftType: 'CITIZEN',
          level: 4,
          growth: 'CITIZEN',
          count: 0,
          boost: 0,
        },
      ],
      petBoostDetailList: [],
    },
    initialDataUpdatedAt: 0,
    ...options,
  });

export const challengeBoostCriteriaUrl = '/api/citizen/v1/purchase/rewards/criteria';
export const getChallengeBoostCriteria = async () => {
  const { data } = await axios.get<ChallengeBoostCriteria>(challengeBoostCriteriaUrl);
  return data;
};
export const useChallengeBoostCriteria = (
  options?: UseDosiminQueryOptions<ChallengeBoostCriteria>
) =>
  useQuery<ChallengeBoostCriteria>({
    queryKey: ['challenge', 'boost', 'criteria'],
    queryFn: getChallengeBoostCriteria,
    initialData: {
      maxBoost: 0,
      maxPetBoost: 0,
      maxBoostPerLevelCount: 0,
      boostDetailList: [],
    },
    ...options,
  });

export type ChallengeRewardCriteria = {
  totalRewardAmount: number;
  rewardCurrency: Currency;
  minPurchaseAmountUsd: number;
  topGroupShare: number;
  bottomGroupShare: number;
  maxPurchaseRewardRate: number;
  topGroupMaxPurchaseRewardRate: number;
  bottomGroupMaxPurchaseRewardRate: number;
};

export const challengeRewardCriteriaUrl = '/api/citizen/v1/challenge/rewards/criteria';

export const getChallengeRewardCriteria = async () => {
  const { data } = await axios.get<ChallengeRewardCriteria>(challengeRewardCriteriaUrl);
  return data;
};

export const useChallengeRewardCriteria = (
  options?: UseDosiminQueryOptions<ChallengeRewardCriteria>
) =>
  useQuery<ChallengeRewardCriteria>({
    queryKey: ['challenge', 'reward', 'criteria'],
    queryFn: getChallengeRewardCriteria,
    initialData: {
      totalRewardAmount: 0,
      rewardCurrency: 'KLAY',
      minPurchaseAmountUsd: 0,
      topGroupShare: 0,
      bottomGroupShare: 0,
      maxPurchaseRewardRate: 0,
      topGroupMaxPurchaseRewardRate: 0,
      bottomGroupMaxPurchaseRewardRate: 0,
    },
    useErrorBoundary: false,
    ...options,
  });
